/**
 * Root-Cause Analysis - Step 3: Filter & Statistics
 *
 * Allows users to apply local filters (date range, edge duration, edge containment, activity nodes)
 * and view attribute value statistics for selected object types and attributes.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { makePostRequest } from '../../../services/apiClient';
import { apiClient } from '../../../services/apiClient';
import { getOCELMetadata } from '../../../services/api/ocel.api';
import DateRangePresets from '../../filter/TabFilterModal/DateRangePresets';

/**
 * FilterSection component for collapsible accordion sections
 */
const FilterSection = ({ title, sectionKey, children, activeCount = null, expandedSections, toggleSection }) => {
  const isExpanded = expandedSections[sectionKey];

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden mb-4">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {/* Chevron icon */}
          <svg
            className={`w-4 h-4 text-gray-600 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-medium text-gray-700">{title}</span>
          {activeCount !== null && activeCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
              {activeCount}
            </span>
          )}
        </div>
      </button>
      {isExpanded && (
        <div className="px-4 py-3 bg-white border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * Helper function to group statistics by object type and attribute
 * Returns: { objectType: { attribute: [stats], attribute2: [stats] }, ... }
 */
const groupStatisticsByObjectTypeAndAttribute = (statistics) => {
  const grouped = {};

  statistics.forEach(stat => {
    if (!grouped[stat.object_type]) {
      grouped[stat.object_type] = {};
    }
    if (!grouped[stat.object_type][stat.attribute]) {
      grouped[stat.object_type][stat.attribute] = [];
    }
    grouped[stat.object_type][stat.attribute].push(stat);
  });

  // Sort values within each attribute by count descending
  Object.keys(grouped).forEach(objType => {
    Object.keys(grouped[objType]).forEach(attr => {
      grouped[objType][attr].sort((a, b) => b.count - a.count);
    });
  });

  return grouped;
};

const Step3_FilterAndStats = ({
  activeFile,
  selectedObjectTypes,
  selectedAttributes,
  initialDateRange,
  initialEdgeDurationFilters,
  initialEdgeContainmentFilters,
  initialActivityNodeFilters,
  initialEventAttributeFilters,
  initialObjectAttributeFilters,
  initialStatistics,
  initialExpandedSections,
  onBack,
  onNext,
}) => {
  // State for accordion sections — restore saved state, or auto-expand sections with active filters
  const [expandedSections, setExpandedSections] = useState(() => {
    if (initialExpandedSections && Object.values(initialExpandedSections).some(Boolean)) {
      return initialExpandedSections;
    }
    return {
      dateRange: !!(initialDateRange?.enabled && (initialDateRange?.startDate || initialDateRange?.endDate)),
      edgeFilters: !!((initialEdgeDurationFilters?.length > 0) || (initialEdgeContainmentFilters?.edges?.length > 0)),
      activityNodeFilters: !!(initialActivityNodeFilters?.nodes?.length > 0),
      eventAttributeFilters: !!(initialEventAttributeFilters?.enabled && initialEventAttributeFilters?.conditions?.some(c => c.attribute && c.value)),
      objectAttributeFilters: !!(initialObjectAttributeFilters?.enabled && initialObjectAttributeFilters?.conditions?.some(c => c.attribute && c.value)),
    };
  });

  // State for root cause analysis
  const [analyzingRootCause, setAnalyzingRootCause] = useState(false);

  // State for filters — seeded from parent when navigating back
  const [dateRange, setDateRange] = useState(
    initialDateRange ?? { startDate: null, endDate: null, enabled: false }
  );
  const [datasetBounds, setDatasetBounds] = useState(null);
  const [edgeDurationFilters, setEdgeDurationFilters] = useState(initialEdgeDurationFilters ?? []);
  const [edgeFilterMode, setEdgeFilterMode] = useState('duration'); // 'duration' or 'containment'
  const [edgeContainmentFilters, setEdgeContainmentFilters] = useState(
    initialEdgeContainmentFilters ?? { enabled: false, edges: [], matchType: 'any', mode: 'include' }
  );
  const [activityNodeFilters, setActivityNodeFilters] = useState(
    initialActivityNodeFilters ?? { enabled: false, nodes: [], matchType: 'any', mode: 'include' }
  );
  const [availableActivities, setAvailableActivities] = useState([]);
  const [eventAttributeFilters, setEventAttributeFilters] = useState(
    initialEventAttributeFilters ?? { enabled: false, mode: 'include', matchType: 'any', conditions: [] }
  );
  const [availableEventAttributes, setAvailableEventAttributes] = useState([]);
  const [eventAttributeValues, setEventAttributeValues] = useState({});
  const [loadingEventAttrValues, setLoadingEventAttrValues] = useState({});
  const [objectAttributeFilters, setObjectAttributeFilters] = useState(
    initialObjectAttributeFilters ?? { enabled: false, mode: 'include', matchType: 'any', conditions: [] }
  );
  const [availableObjectAttributes, setAvailableObjectAttributes] = useState([]);
  const [objectAttributeValues, setObjectAttributeValues] = useState({});
  const [loadingObjAttrValues, setLoadingObjAttrValues] = useState({});

  // Accordion control functions
  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const expandAll = () => {
    setExpandedSections({
      dateRange: true,
      edgeFilters: true,
      activityNodeFilters: true,
      eventAttributeFilters: true,
      objectAttributeFilters: true,
    });
  };

  const collapseAll = () => {
    setExpandedSections({
      dateRange: false,
      edgeFilters: false,
      activityNodeFilters: false,
      eventAttributeFilters: false,
      objectAttributeFilters: false,
    });
  };

  // State for results — restore from parent when navigating back
  const [statistics, setStatistics] = useState(initialStatistics || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasAnalyzed, setHasAnalyzed] = useState(!!initialStatistics);

  // Fetch dataset bounds on mount
  useEffect(() => {
    fetchDatasetBounds();
    fetchAvailableActivities();
  }, [activeFile]);

  const fetchDatasetBounds = async () => {
    if (!activeFile) return;
    try {
      const metadata = await getOCELMetadata(activeFile);

      if (metadata && metadata.date_range) {
        setDatasetBounds({
          startDate: metadata.date_range.start ? metadata.date_range.start.split('T')[0] : null,
          endDate: metadata.date_range.end ? metadata.date_range.end.split('T')[0] : null
        });
        console.log('Dataset date bounds:', metadata.date_range);
      }
    } catch (err) {
      console.error('Error fetching dataset bounds:', err);
    }
  };

  const fetchAvailableActivities = async () => {
    if (!activeFile) return;
    try {
      const metadata = await getOCELMetadata(activeFile);

      if (metadata && metadata.event_types) {
        const activities = metadata.event_types.map(et =>
          (typeof et === 'object' && et !== null && et.name) ? et.name : et
        );
        setAvailableActivities(activities);
      }

      if (metadata && metadata.event_attribute_names) {
        const attrs = Array.isArray(metadata.event_attribute_names)
          ? metadata.event_attribute_names
          : Object.keys(metadata.event_attribute_names);
        setAvailableEventAttributes(attrs);
      }

      if (metadata && metadata.object_attribute_names) {
        const attrs = Array.isArray(metadata.object_attribute_names)
          ? metadata.object_attribute_names
          : Object.keys(metadata.object_attribute_names);
        setAvailableObjectAttributes(attrs);
      }
    } catch (err) {
      console.error('Error fetching activities/attributes:', err);
    }
  };

  const fetchEventAttributeValues = useCallback((attribute) => {
    if (!activeFile || !attribute || eventAttributeValues[attribute]) return;
    setLoadingEventAttrValues(prev => ({ ...prev, [attribute]: true }));
    apiClient
      .get(`/ocel/event-attributes/${encodeURIComponent(attribute)}/values`, {
        params: { file_type: activeFile },
      })
      .then(res => {
        setEventAttributeValues(prev => ({ ...prev, [attribute]: res.data || [] }));
      })
      .catch(() => {
        setEventAttributeValues(prev => ({ ...prev, [attribute]: [] }));
      })
      .finally(() => {
        setLoadingEventAttrValues(prev => ({ ...prev, [attribute]: false }));
      });
  }, [activeFile, eventAttributeValues]);

  const fetchObjectAttributeValues = useCallback((attribute) => {
    if (!activeFile || !attribute || objectAttributeValues[attribute]) return;
    setLoadingObjAttrValues(prev => ({ ...prev, [attribute]: true }));
    apiClient
      .get(`/ocel/object-attributes/${encodeURIComponent(attribute)}/values`, {
        params: { file_type: activeFile },
      })
      .then(res => {
        setObjectAttributeValues(prev => ({ ...prev, [attribute]: res.data || [] }));
      })
      .catch(() => {
        setObjectAttributeValues(prev => ({ ...prev, [attribute]: [] }));
      })
      .finally(() => {
        setLoadingObjAttrValues(prev => ({ ...prev, [attribute]: false }));
      });
  }, [activeFile, objectAttributeValues]);

  // Backfill value lists for attributes already selected on mount (e.g. restored
  // after step 2 -> step 3 back/forward navigation, where local state resets but
  // the parent-provided conditions/attribute selection survives).
  useEffect(() => {
    eventAttributeFilters.conditions.forEach(cond => {
      if (cond.attribute && !eventAttributeValues[cond.attribute]) {
        fetchEventAttributeValues(cond.attribute);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventAttributeFilters.conditions]);

  useEffect(() => {
    objectAttributeFilters.conditions.forEach(cond => {
      if (cond.attribute && !objectAttributeValues[cond.attribute]) {
        fetchObjectAttributeValues(cond.attribute);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectAttributeFilters.conditions]);

  // Listen for edge click events from the graph (RCA-specific events)
  useEffect(() => {
    const handleEdgeClick = (event) => {
      const { edgeId, edgeLabel, scopeKind, objectType, sourceActivity, targetActivity } = event.detail;

      // Route to appropriate filter based on current mode
      if (edgeFilterMode === 'containment') {
        console.log('🔍 RCA Step 3 - Edge clicked:', {
          sourceActivity,
          targetActivity,
          edgeLabel
        });

        const newEdge = {
          source_activity: sourceActivity,
          target_activity: targetActivity,
          edge_label: edgeLabel
        };

        // Check for duplicates
        const isDuplicate = edgeContainmentFilters.edges.some(
          edge => edge.source_activity === sourceActivity &&
                  edge.target_activity === targetActivity
        );

        if (!isDuplicate) {
          setEdgeContainmentFilters(prev => ({
            ...prev,
            edges: [...prev.edges, newEdge],
            enabled: true
          }));
        } else {
          console.log('Edge already exists in containment filter');
        }
      } else {
        // Duration filter mode
        console.log('🔍 RCA Step 3 - Edge duration clicked:', { edgeId, edgeLabel, scopeKind, objectType, sourceActivity, targetActivity });

        const newFilter = {
          edge_id: edgeId,
          edge_label: edgeLabel,
          scope_kind: scopeKind,
          object_type: objectType,
          source_activity: sourceActivity,
          target_activity: targetActivity,
          operator: 'le',
          value: '',
          unit: 'hours',
          threshold_ms: null,
          mode: 'include'
        };

        // Replace existing filter (only one edge filter at a time for simplicity)
        setEdgeDurationFilters([newFilter]);
      }
    };

    // Listen for RCA-specific event
    window.addEventListener('rca:edge-duration-filter:create', handleEdgeClick);
    return () => window.removeEventListener('rca:edge-duration-filter:create', handleEdgeClick);
  }, [edgeFilterMode, edgeContainmentFilters.edges]);

  // Dispatch edge filter mode changes to ReactFlowViewer
  useEffect(() => {
    console.log('🎯 RCA Step 3: Edge filter mode changed to', edgeFilterMode);
    window.dispatchEvent(new CustomEvent('rca:edge-filter-mode:changed', {
      detail: { edgeFilterMode }
    }));
  }, [edgeFilterMode]);

  // Dispatch edge filter changes for highlighting in ReactFlowViewer
  useEffect(() => {
    console.log('🔄 RCA Step 3: Edge filters changed', {
      containment: edgeContainmentFilters.edges,
      duration: edgeDurationFilters,
      mode: edgeFilterMode
    });
    window.dispatchEvent(new CustomEvent('rca:edge-highlighting:update', {
      detail: {
        containmentEdges: edgeContainmentFilters.edges,
        durationFilters: edgeDurationFilters,
        mode: edgeFilterMode
      }
    }));
  }, [edgeContainmentFilters.edges, edgeDurationFilters, edgeFilterMode]);

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    setHasAnalyzed(true);

    try {
      const payload = {
        file_type: activeFile,
        selected_object_types: selectedObjectTypes,
        selected_attributes: selectedAttributes,

        // Date range (only if enabled)
        date_range: dateRange.enabled && (dateRange.startDate || dateRange.endDate) ? {
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          enabled: true
        } : undefined,

        // Edge duration filters
        edge_duration_filters: edgeDurationFilters.length > 0 ? edgeDurationFilters : undefined,

        // Edge containment filters (auto-active when edges exist)
        edge_containment_filters: (edgeContainmentFilters.edges && edgeContainmentFilters.edges.length > 0)
          ? edgeContainmentFilters
          : undefined,

        // Activity node filters (auto-active when nodes exist)
        activity_node_filters: (activityNodeFilters.nodes && activityNodeFilters.nodes.length > 0)
          ? activityNodeFilters
          : undefined,

        // Event attribute filter (active when enabled with complete conditions)
        event_attribute_filter: (eventAttributeFilters.enabled && eventAttributeFilters.conditions.some(c => c.attribute && c.value))
          ? eventAttributeFilters
          : undefined,

        // Object attribute filter (active when enabled with complete conditions)
        object_attribute_filter: (objectAttributeFilters.enabled && objectAttributeFilters.conditions.some(c => c.attribute && c.value))
          ? objectAttributeFilters
          : undefined,
      };

      console.log('Analyzing with payload:', payload);

      const response = await makePostRequest('/rootcause/statistics', payload);
      console.log('Statistics response:', response);
      setStatistics(response);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError(err.message || 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setDateRange({ startDate: null, endDate: null, enabled: false });
    setEdgeDurationFilters([]);
    setEdgeContainmentFilters({ edges: [], matchType: 'any', mode: 'include', enabled: false });
    setActivityNodeFilters({ nodes: [], matchType: 'any', mode: 'include', enabled: false });
    setEventAttributeFilters({ enabled: false, mode: 'include', matchType: 'any', conditions: [] });
    setObjectAttributeFilters({ enabled: false, mode: 'include', matchType: 'any', conditions: [] });
  };

  const handleAddEdgeFilter = () => {
    setEdgeDurationFilters([...edgeDurationFilters, {
      edge_id: '',
      edge_label: '',
      operator: 'le',
      value: '',
      unit: 'hours',
      threshold_ms: null,
      mode: 'include'
    }]);
  };

  const handleRemoveEdgeFilter = (index) => {
    setEdgeDurationFilters(edgeDurationFilters.filter((_, i) => i !== index));
  };

  const handleUpdateEdgeFilter = (index, updates) => {
    const newFilters = [...edgeDurationFilters];
    newFilters[index] = { ...newFilters[index], ...updates };

    // Recalculate threshold_ms when value or unit changes
    if (updates.value !== undefined || updates.unit !== undefined) {
      const value = parseFloat(updates.value !== undefined ? updates.value : newFilters[index].value);
      const unit = updates.unit || newFilters[index].unit;

      if (!isNaN(value)) {
        const unitMultipliers = {
          seconds: 1000,
          minutes: 60 * 1000,
          hours: 60 * 60 * 1000,
          days: 24 * 60 * 60 * 1000,
          weeks: 7 * 24 * 60 * 60 * 1000,
          months: 30 * 24 * 60 * 60 * 1000
        };
        newFilters[index].threshold_ms = value * unitMultipliers[unit];
      }
    }

    setEdgeDurationFilters(newFilters);
  };

  const handleRemoveEdgeFromContainment = (index) => {
    setEdgeContainmentFilters(prev => ({
      ...prev,
      edges: prev.edges.filter((_, i) => i !== index),
      enabled: prev.edges.filter((_, i) => i !== index).length > 0
    }));
  };

  // No longer needed - filters are auto-active when edges are selected
  // Kept for backward compatibility but does nothing
  const handleApplyEdgeContainment = () => {
    // Auto-active: filters apply automatically when edges are selected
    console.log('Edge filter is auto-active - no action needed');
  };

  const handleAddActivityNode = (activity) => {
    if (!activityNodeFilters.nodes.includes(activity)) {
      setActivityNodeFilters(prev => ({
        ...prev,
        nodes: [...prev.nodes, activity],
        enabled: true
      }));
    }
  };

  const handleRemoveActivityNode = (index) => {
    setActivityNodeFilters(prev => ({
      ...prev,
      nodes: prev.nodes.filter((_, i) => i !== index),
      enabled: prev.nodes.filter((_, i) => i !== index).length > 0
    }));
  };

  // No longer needed - filters are auto-active when nodes are selected
  // Kept for backward compatibility but does nothing
  const handleApplyActivityNodeFilter = () => {
    // Auto-active: filters apply automatically when activities are selected
    console.log('Activity node filter is auto-active - no action needed');
  };

  const handleEAFConditionChange = (index, field, newVal) => {
    setEventAttributeFilters(prev => {
      const next = [...prev.conditions];
      next[index] = { ...next[index], [field]: newVal };
      if (field === 'attribute') {
        next[index].value = '';
        if (newVal) fetchEventAttributeValues(newVal);
      }
      return { ...prev, conditions: next };
    });
  };

  const handleAddEAFCondition = () => {
    setEventAttributeFilters(prev => ({
      ...prev,
      conditions: [...prev.conditions, { attribute: '', value: '' }],
    }));
  };

  const handleRemoveEAFCondition = (index) => {
    setEventAttributeFilters(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  const handleOAFConditionChange = (index, field, newVal) => {
    setObjectAttributeFilters(prev => {
      const next = [...prev.conditions];
      next[index] = { ...next[index], [field]: newVal };
      if (field === 'attribute') {
        next[index].value = '';
        if (newVal) fetchObjectAttributeValues(newVal);
      }
      return { ...prev, conditions: next };
    });
  };

  const handleAddOAFCondition = () => {
    setObjectAttributeFilters(prev => ({
      ...prev,
      conditions: [...prev.conditions, { attribute: '', value: '' }],
    }));
  };

  const handleRemoveOAFCondition = (index) => {
    setObjectAttributeFilters(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  // Filter activation logic:
  // - Date range: Requires explicit 'enabled' flag (user must select dates)
  // - Edge duration: Auto-active when array has filters
  // - Edge containment: Auto-active when edges array has items
  // - Activity nodes: Auto-active when nodes array has items
  // - Event attribute: Active when enabled and at least one complete condition
  const hasActiveFilters =
    (dateRange.enabled && (dateRange.startDate || dateRange.endDate)) ||
    edgeDurationFilters.length > 0 ||
    (edgeContainmentFilters.edges && edgeContainmentFilters.edges.length > 0) ||
    (activityNodeFilters.nodes && activityNodeFilters.nodes.length > 0) ||
    (eventAttributeFilters.enabled && eventAttributeFilters.conditions.some(c => c.attribute && c.value)) ||
    (objectAttributeFilters.enabled && objectAttributeFilters.conditions.some(c => c.attribute && c.value));

  return (
    <div className="flex flex-col p-6">
      {/* Header */}
      <div className="mb-4 flex-shrink-0">
        <h2 className="text-xl font-semibold text-gray-800">Step 3: Filter & Statistics</h2>
        <p className="text-sm text-gray-600 mt-1">
          Apply filters and view attribute value statistics
        </p>
      </div>

      {/* Selected Object Types & Attributes Summary */}
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex-shrink-0">
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-700 mb-1">
            Selected Object Types ({selectedObjectTypes.length}):
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedObjectTypes.map((objType) => (
              <span
                key={objType}
                className="inline-block px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded"
              >
                {objType}
              </span>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-gray-700 mb-1">
            Selected Attributes ({selectedAttributes.length}):
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedAttributes.map((attr, idx) => (
              <span
                key={`${attr.object_type}-${attr.attribute_name}-${idx}`}
                className="inline-block px-2 py-1 text-xs bg-blue-600 text-white rounded"
              >
                {attr.object_type}: {attr.attribute_name}
              </span>
            ))}
          </div>
        </div>

        <button
          onClick={() => onBack({ dateRange, edgeDurationFilters, edgeContainmentFilters, activityNodeFilters, eventAttributeFilters, objectAttributeFilters, statistics, expandedSections })}
          className="mt-3 text-sm text-blue-600 hover:underline"
        >
          ← Change Selection
        </button>
      </div>

      {/* Global Filter Controls */}
      <div className="mb-4 flex justify-between items-center flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={expandAll}
            className="text-xs text-blue-600 hover:underline"
          >
            Expand All Filters
          </button>
          <button
            onClick={collapseAll}
            className="text-xs text-blue-600 hover:underline"
          >
            Collapse All Filters
          </button>
        </div>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="text-xs text-red-600 hover:underline"
          >
            Clear All Filters
          </button>
        )}
      </div>

      {/* Scrollable container for filters and results */}
      <div className="flex-1 overflow-y-auto" style={{ maxHeight: '800px' }}>
        {/* Date Range Filter */}
        <FilterSection
          title="Date Range Filter (Optional)"
          sectionKey="dateRange"
          activeCount={dateRange.enabled && (dateRange.startDate || dateRange.endDate) ? 1 : 0}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        >
          {datasetBounds && (
            <p className="text-xs text-gray-500 mb-2">
              Dataset range: {datasetBounds.startDate} to {datasetBounds.endDate}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Start Date</label>
              <input
                type="date"
                value={dateRange.startDate || ''}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value || null, enabled: e.target.value !== '' }))}
                min={datasetBounds?.startDate}
                max={datasetBounds?.endDate}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">End Date</label>
              <input
                type="date"
                value={dateRange.endDate || ''}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value || null }))}
                min={datasetBounds?.startDate}
                max={datasetBounds?.endDate}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <DateRangePresets value={dateRange} onChange={setDateRange} />
        </FilterSection>

      {/* Edge Filters (Consolidated) */}
      <FilterSection
        title="Edge Filters (Optional)"
        sectionKey="edgeFilters"
        activeCount={edgeDurationFilters.length + edgeContainmentFilters.edges.length}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
      >
        {/* Mode Switcher */}
        <div className="mb-4">
          <p className="text-xs text-gray-600 mb-2">
            Choose filter mode (affects what happens when you click edges in the graph):
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setEdgeFilterMode('duration')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded border ${
                edgeFilterMode === 'duration'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Edge Duration Filter
            </button>
            <button
              onClick={() => setEdgeFilterMode('containment')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded border ${
                edgeFilterMode === 'containment'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Edge Filter
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {edgeFilterMode === 'duration'
              ? '✓ Click edges to filter by transition duration (time threshold)'
              : '✓ Click edges to filter objects by edge'}
          </p>
        </div>

        {/* Conditional rendering based on mode */}
        {edgeFilterMode === 'duration' ? (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-xs font-semibold text-gray-700 mb-3">Duration Filters</h4>

            {edgeDurationFilters.length === 0 && (
              <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="font-medium text-blue-700 mb-1">How to add an edge duration filter:</p>
                <p><strong>Click on an edge</strong> (arrow between activities) in the Process Graph below.</p>
              </div>
            )}

            {edgeDurationFilters.length > 0 && (
              <div className="space-y-3">
                {edgeDurationFilters.map((filter, index) => (
                  <div key={index} className="border border-gray-300 rounded-lg p-3 bg-gray-50">
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      {/* Selected Edge (Read-Only) */}
                      <div className="col-span-2">
                        <label className="block text-xs text-gray-600 mb-1">Selected Edge (Activity Transition)</label>
                        <div className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md text-gray-700 font-medium">
                          ✓ {filter.edge_label || `${filter.edge_id}`}
                        </div>
                      </div>

                      {/* Operator */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Operator</label>
                        <select
                          value={filter.operator}
                          onChange={(e) => handleUpdateEdgeFilter(index, { operator: e.target.value })}
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="le">≤ Less than or equal</option>
                          <option value="ge">≥ Greater than or equal</option>
                        </select>
                      </div>

                      {/* Mode */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Mode</label>
                        <select
                          value={filter.mode}
                          onChange={(e) => handleUpdateEdgeFilter(index, { mode: e.target.value })}
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="include">Include matching</option>
                          <option value="exclude">Exclude matching</option>
                        </select>
                      </div>

                      {/* Value */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Threshold Value</label>
                        <input
                          type="number"
                          value={filter.value}
                          onChange={(e) => handleUpdateEdgeFilter(index, { value: e.target.value })}
                          placeholder="e.g., 48"
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Unit */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Time Unit</label>
                        <select
                          value={filter.unit}
                          onChange={(e) => handleUpdateEdgeFilter(index, { unit: e.target.value })}
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="seconds">Seconds</option>
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                          <option value="days">Days</option>
                          <option value="weeks">Weeks</option>
                          <option value="months">Months</option>
                        </select>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemoveEdgeFilter(index)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Remove Filter
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-xs font-semibold text-gray-700 mb-3">Edge Filter</h4>

            {edgeContainmentFilters.edges.length === 0 && (
              <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="font-medium text-blue-700 mb-1">How to add an edge filter:</p>
                <p><strong>Click on edges</strong> in the Process Graph below to add them to this filter.</p>
              </div>
            )}

            {edgeContainmentFilters.edges.length > 0 && (
              <div className="space-y-3">
                {/* Selected Edges */}
                <div>
                  <label className="block text-xs text-gray-600 mb-2">Selected Edges ({edgeContainmentFilters.edges.length})</label>
                  <div className="space-y-2">
                    {edgeContainmentFilters.edges.map((edge, index) => (
                      <div key={index} className="flex items-center justify-between bg-white border border-gray-300 rounded-md px-3 py-2">
                        <span className="text-xs font-medium text-gray-700">{edge.edge_label}</span>
                        <button
                          onClick={() => handleRemoveEdgeFromContainment(index)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Match Type */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Match Type</label>
                  <select
                    value={edgeContainmentFilters.matchType}
                    onChange={(e) => setEdgeContainmentFilters(prev => ({ ...prev, matchType: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="any">Any (match at least one edge)</option>
                    <option value="all">All (match all edges)</option>
                  </select>
                </div>

                {/* Mode */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Mode</label>
                  <select
                    value={edgeContainmentFilters.mode}
                    onChange={(e) => setEdgeContainmentFilters(prev => ({ ...prev, mode: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="include">Include matching objects</option>
                    <option value="exclude">Exclude matching objects</option>
                  </select>
                </div>

                {/* Status Indicator */}
                {edgeContainmentFilters.edges.length > 0 && (
                  <div className="w-full px-4 py-2 text-sm font-medium rounded bg-blue-100 text-blue-700 border border-blue-300">
                    ✓ {edgeContainmentFilters.edges.length} edge{edgeContainmentFilters.edges.length !== 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </FilterSection>

      {/* Activity Node Filter */}
      <FilterSection
        title="Activity Node Filter (Optional)"
        sectionKey="activityNodeFilters"
        activeCount={activityNodeFilters.nodes.length}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
      >
        {availableActivities.length === 0 ? (
          <div className="text-xs text-gray-500 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="font-medium text-yellow-700 mb-1">⚠️ No activities available</p>
            <p>Could not load activity list from OCEL metadata.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Activity Selector */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">Select Activities</label>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleAddActivityNode(e.target.value);
                    e.target.value = '';
                  }
                }}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                defaultValue=""
              >
                <option value="" disabled>-- Select an activity --</option>
                {availableActivities
                  .filter(activity => !activityNodeFilters.nodes.includes(activity))
                  .map((activity) => (
                    <option key={activity} value={activity}>
                      {activity}
                    </option>
                  ))}
              </select>
            </div>

            {/* Selected Activities */}
            {activityNodeFilters.nodes.length > 0 && (
              <div>
                <label className="block text-xs text-gray-600 mb-2">Selected Activities ({activityNodeFilters.nodes.length})</label>
                <div className="space-y-2">
                  {activityNodeFilters.nodes.map((node, index) => (
                    <div key={index} className="flex items-center justify-between bg-white border border-gray-300 rounded-md px-3 py-2">
                      <span className="text-xs font-medium text-gray-700">{node}</span>
                      <button
                        onClick={() => handleRemoveActivityNode(index)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activityNodeFilters.nodes.length > 0 && (
              <>
                {/* Match Type */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Match Type</label>
                  <select
                    value={activityNodeFilters.matchType}
                    onChange={(e) => setActivityNodeFilters(prev => ({ ...prev, matchType: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="any">Any (match at least one activity)</option>
                    <option value="all">All (match all activities)</option>
                  </select>
                </div>

                {/* Mode */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Mode</label>
                  <select
                    value={activityNodeFilters.mode}
                    onChange={(e) => setActivityNodeFilters(prev => ({ ...prev, mode: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="include">Include matching objects</option>
                    <option value="exclude">Exclude matching objects</option>
                  </select>
                </div>

                {/* Status Indicator */}
                {activityNodeFilters.nodes.length > 0 && (
                  <div className="w-full px-4 py-2 text-sm font-medium rounded bg-blue-100 text-blue-700 border border-blue-300">
                    ✓ {activityNodeFilters.nodes.length} activit{activityNodeFilters.nodes.length !== 1 ? 'ies' : 'y'} selected
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </FilterSection>

      {/* Event Attribute Filter */}
      <FilterSection
        title="Event Attribute Filter (Optional)"
        sectionKey="eventAttributeFilters"
        activeCount={eventAttributeFilters.enabled ? eventAttributeFilters.conditions.filter(c => c.attribute && c.value).length : 0}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
      >
        <div className="space-y-3">
          {/* Enabled toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={eventAttributeFilters.enabled}
              onChange={(e) => setEventAttributeFilters(prev => ({ ...prev, enabled: e.target.checked }))}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-xs font-medium text-gray-700">
              Filter objects by linked event attributes
            </span>
          </label>

          {/* Mode radio */}
          <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
            <span className="text-xs font-medium text-gray-700">Mode:</span>
            {['include', 'exclude'].map(m => (
              <label key={m} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="rca-eaf-mode"
                  value={m}
                  checked={eventAttributeFilters.mode === m}
                  onChange={() => setEventAttributeFilters(prev => ({ ...prev, mode: m }))}
                  disabled={!eventAttributeFilters.enabled}
                  className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-700">{m === 'include' ? 'Include' : 'Exclude'}</span>
              </label>
            ))}
          </div>

          {/* Match radio */}
          <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
            <span className="text-xs font-medium text-gray-700">Match:</span>
            {['any', 'all'].map(m => (
              <label key={m} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="rca-eaf-match"
                  value={m}
                  checked={eventAttributeFilters.matchType === m}
                  onChange={() => setEventAttributeFilters(prev => ({ ...prev, matchType: m }))}
                  disabled={!eventAttributeFilters.enabled}
                  className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-700">{m === 'any' ? 'Any' : 'All'}</span>
              </label>
            ))}
          </div>

          {/* Condition rows */}
          <div className="space-y-2">
            {eventAttributeFilters.conditions.map((cond, idx) => {
              const vals = eventAttributeValues[cond.attribute] || [];
              const isLoadingVals = loadingEventAttrValues[cond.attribute];
              const isDisabled = !eventAttributeFilters.enabled;
              return (
                <div key={idx} className="flex items-start gap-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                  <div className="flex-1 space-y-2">
                    <select
                      value={cond.attribute}
                      onChange={(e) => handleEAFConditionChange(idx, 'attribute', e.target.value)}
                      disabled={isDisabled || availableEventAttributes.length === 0}
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select attribute…</option>
                      {availableEventAttributes.map(a => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>

                    {isLoadingVals ? (
                      <div className="text-xs text-gray-500 italic py-1">Loading values…</div>
                    ) : cond.attribute ? (
                      <select
                        value={cond.value}
                        onChange={(e) => handleEAFConditionChange(idx, 'value', e.target.value)}
                        disabled={isDisabled || !cond.attribute}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select value…</option>
                        {vals.map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveEAFCondition(idx)}
                    disabled={isDisabled}
                    className="mt-1 text-red-500 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                    title="Remove condition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={handleAddEAFCondition}
            disabled={!eventAttributeFilters.enabled}
            className="w-full px-3 py-2 text-xs font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            + Add condition
          </button>

          {!eventAttributeFilters.enabled && (
            <p className="text-xs text-gray-500 italic">Enable filter to add conditions</p>
          )}

          {eventAttributeFilters.enabled && eventAttributeFilters.conditions.filter(c => c.attribute && c.value).length > 0 && (
            <div className="w-full px-4 py-2 text-sm font-medium rounded bg-blue-100 text-blue-700 border border-blue-300">
              ✓ {eventAttributeFilters.conditions.filter(c => c.attribute && c.value).length} condition(s) active
            </div>
          )}
        </div>
      </FilterSection>

      {/* Object Attribute Filter */}
      <FilterSection
        title="Object Attribute Filter (Optional)"
        sectionKey="objectAttributeFilters"
        activeCount={objectAttributeFilters.enabled ? objectAttributeFilters.conditions.filter(c => c.attribute && c.value).length : 0}
        expandedSections={expandedSections}
        toggleSection={toggleSection}
      >
        <div className="space-y-3">
          {/* Enabled toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={objectAttributeFilters.enabled}
              onChange={(e) => setObjectAttributeFilters(prev => ({ ...prev, enabled: e.target.checked }))}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-xs font-medium text-gray-700">
              Filter objects by their own attribute values
            </span>
          </label>

          {/* Mode radio */}
          <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
            <span className="text-xs font-medium text-gray-700">Mode:</span>
            {['include', 'exclude'].map(m => (
              <label key={m} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="rca-oaf-mode"
                  value={m}
                  checked={objectAttributeFilters.mode === m}
                  onChange={() => setObjectAttributeFilters(prev => ({ ...prev, mode: m }))}
                  disabled={!objectAttributeFilters.enabled}
                  className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-700">{m === 'include' ? 'Include' : 'Exclude'}</span>
              </label>
            ))}
          </div>

          {/* Match radio */}
          <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
            <span className="text-xs font-medium text-gray-700">Match:</span>
            {['any', 'all'].map(m => (
              <label key={m} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="rca-oaf-match"
                  value={m}
                  checked={objectAttributeFilters.matchType === m}
                  onChange={() => setObjectAttributeFilters(prev => ({ ...prev, matchType: m }))}
                  disabled={!objectAttributeFilters.enabled}
                  className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-700">{m === 'any' ? 'Any' : 'All'}</span>
              </label>
            ))}
          </div>

          {/* Condition rows */}
          <div className="space-y-2">
            {objectAttributeFilters.conditions.map((cond, idx) => {
              const vals = objectAttributeValues[cond.attribute] || [];
              const isLoadingVals = loadingObjAttrValues[cond.attribute];
              const isDisabled = !objectAttributeFilters.enabled;
              return (
                <div key={idx} className="flex items-start gap-2 p-3 bg-gray-50 rounded-md border border-gray-200">
                  <div className="flex-1 space-y-2">
                    <select
                      value={cond.attribute}
                      onChange={(e) => handleOAFConditionChange(idx, 'attribute', e.target.value)}
                      disabled={isDisabled || availableObjectAttributes.length === 0}
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select attribute…</option>
                      {availableObjectAttributes.map(a => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>

                    {isLoadingVals ? (
                      <div className="text-xs text-gray-500 italic py-1">Loading values…</div>
                    ) : cond.attribute ? (
                      <select
                        value={cond.value}
                        onChange={(e) => handleOAFConditionChange(idx, 'value', e.target.value)}
                        disabled={isDisabled || !cond.attribute}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select value…</option>
                        {vals.map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveOAFCondition(idx)}
                    disabled={isDisabled}
                    className="mt-1 text-red-500 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                    title="Remove condition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={handleAddOAFCondition}
            disabled={!objectAttributeFilters.enabled}
            className="w-full px-3 py-2 text-xs font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            + Add condition
          </button>

          {!objectAttributeFilters.enabled && (
            <p className="text-xs text-gray-500 italic">Enable filter to add conditions</p>
          )}

          {objectAttributeFilters.enabled && objectAttributeFilters.conditions.filter(c => c.attribute && c.value).length > 0 && (
            <div className="w-full px-4 py-2 text-sm font-medium rounded bg-blue-100 text-blue-700 border border-blue-300">
              ✓ {objectAttributeFilters.conditions.filter(c => c.attribute && c.value).length} condition(s) active
            </div>
          )}
        </div>
      </FilterSection>

      {/* Analyze Button */}
      <div className="mb-4">
        <button
          onClick={handleAnalyze}
          disabled={loading}
          className={`w-full px-6 py-3 rounded font-medium ${
            loading
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {loading ? 'Analyzing...' : hasAnalyzed ? 'Re-analyze' : 'Analyze Statistics'}
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 font-medium">Error</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={handleAnalyze}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* Statistics Display */}
      {statistics && !loading && (
        <>
          {/* Single Table View (No Active Filters) */}
          {!statistics.has_active_filters && (() => {
            const grouped = groupStatisticsByObjectTypeAndAttribute(statistics.statistics);
            return (
              <div className="flex-1 overflow-auto border border-gray-300 rounded-lg">
                <div className="p-4 bg-gray-50 border-b border-gray-300">
                  <p className="text-sm font-medium text-gray-700">
                    Results: {statistics.statistics.length} value entries from {statistics.total_objects_analyzed} objects
                  </p>
                  <p className="text-xs text-gray-600 mt-1">No filters applied - showing full OCEL data grouped by object type and attribute</p>
                </div>

                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left border-b font-medium text-gray-700">Object Type</th>
                      <th className="px-4 py-2 text-left border-b font-medium text-gray-700">Attribute</th>
                      <th className="px-4 py-2 text-left border-b font-medium text-gray-700">Value</th>
                      <th className="px-4 py-2 text-right border-b font-medium text-gray-700">Count</th>
                      <th className="px-4 py-2 text-right border-b font-medium text-gray-700">Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(grouped).length > 0 ? (
                      Object.keys(grouped).map((objType, objTypeIndex) => (
                        Object.keys(grouped[objType]).map((attr, attrIndex) => (
                          <React.Fragment key={`${objType}-${attr}`}>
                            {/* Value Rows - Top 10 only, first row has thick top border */}
                            {grouped[objType][attr].slice(0, 10).map((stat, idx) => {
                              const isFirstObjectType = objTypeIndex === 0;
                              const isObjectTypeStart = attrIndex === 0 && idx === 0 && !isFirstObjectType;
                              const isAttributeStart = idx === 0 && attrIndex > 0;

                              return (
                                <tr
                                  key={`${objType}-${attr}-${idx}`}
                                  className="border-b hover:bg-gray-50"
                                >
                                  <td className={`px-4 py-2 ${isObjectTypeStart ? 'border-t-4 border-t-gray-700' : ''}`}>
                                    <span className="inline-block px-2 py-1 text-xs rounded bg-blue-50 text-blue-700">
                                      {objType}
                                    </span>
                                  </td>
                                  <td className={`px-4 py-2 text-gray-700 text-sm ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>{attr}</td>
                                  <td className={`px-4 py-2 text-gray-600 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>{stat.value}</td>
                                  <td className={`px-4 py-2 text-right text-gray-600 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>{stat.count.toLocaleString()}</td>
                                  <td className={`px-4 py-2 text-right text-gray-600 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>{stat.percentage.toFixed(2)}%</td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                          No statistics found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Split-Screen View (With Active Filters) */}
          {statistics.has_active_filters && (
            <>
              <div className="flex-1 grid lg:grid-cols-2 grid-cols-1 gap-4" style={{ maxHeight: '600px' }}>
                {/* LEFT SIDE: Unfiltered (Full OCEL) */}
                {(() => {
                  const groupedUnfiltered = statistics.statistics_unfiltered
                    ? groupStatisticsByObjectTypeAndAttribute(statistics.statistics_unfiltered)
                    : {};
                  return (
                    <div className="border border-gray-300 rounded-lg overflow-auto">
                      <div className="p-3 bg-gray-100 border-b border-gray-300 sticky top-0 z-10">
                        <p className="text-sm font-semibold text-gray-700">
                          Unfiltered
                        </p>
                        <p className="text-xs text-gray-600">
                          {statistics.total_objects_unfiltered?.toLocaleString()} objects total
                        </p>
                      </div>
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-[52px] z-10">
                          <tr>
                            <th className="px-3 py-2 text-left border-b text-xs font-medium text-gray-700">Object Type</th>
                            <th className="px-3 py-2 text-left border-b text-xs font-medium text-gray-700">Attribute</th>
                            <th className="px-3 py-2 text-left border-b text-xs font-medium text-gray-700">Value</th>
                            <th className="px-3 py-2 text-right border-b text-xs font-medium text-gray-700">Count</th>
                            <th className="px-3 py-2 text-right border-b text-xs font-medium text-gray-700">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.keys(groupedUnfiltered).length > 0 ? (
                            Object.keys(groupedUnfiltered).map((objType, objTypeIndex) => (
                              Object.keys(groupedUnfiltered[objType]).map((attr, attrIndex) => (
                                <React.Fragment key={`unfiltered-${objType}-${attr}`}>
                                  {/* Value Rows - Top 10 only, first row has thick top border */}
                                  {groupedUnfiltered[objType][attr].slice(0, 10).map((stat, idx) => {
                                    const isFirstObjectType = objTypeIndex === 0;
                                    const isObjectTypeStart = attrIndex === 0 && idx === 0 && !isFirstObjectType;
                                    const isAttributeStart = idx === 0 && attrIndex > 0;

                                    return (
                                      <tr
                                        key={`unfiltered-${objType}-${attr}-${idx}`}
                                        className="border-b hover:bg-gray-50"
                                      >
                                        <td className={`px-3 py-1 ${isObjectTypeStart ? 'border-t-4 border-t-gray-700' : ''}`}>
                                          <span className="inline-block px-2 py-0.5 text-xs rounded bg-gray-50 text-gray-600">
                                            {objType}
                                          </span>
                                        </td>
                                        <td className={`px-3 py-1 text-xs text-gray-700 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>{attr}</td>
                                        <td className={`px-3 py-1 text-xs text-gray-600 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>{stat.value}</td>
                                        <td className={`px-3 py-1 text-right text-xs text-gray-600 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>{stat.count.toLocaleString()}</td>
                                        <td className={`px-3 py-1 text-right text-xs text-gray-600 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-gray-700' : ''}`}>{stat.percentage.toFixed(2)}%</td>
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              ))
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="px-3 py-8 text-center text-gray-500 text-xs">
                                No unfiltered data
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                {/* RIGHT SIDE: Filtered (After Filters) */}
                {(() => {
                  const groupedFiltered = groupStatisticsByObjectTypeAndAttribute(statistics.statistics);
                  return (
                    <div className="border border-blue-400 rounded-lg overflow-auto">
                      <div className="p-3 bg-blue-100 border-b border-blue-400 sticky top-0 z-10">
                        <p className="text-sm font-semibold text-blue-800">
                          Filtered
                        </p>
                        <p className="text-xs text-blue-700">
                          {statistics.total_objects_analyzed.toLocaleString()} objects
                          {dateRange.startDate && ` • From: ${dateRange.startDate}`}
                          {dateRange.endDate && ` • To: ${dateRange.endDate}`}
                        </p>
                      </div>
                      <table className="w-full text-sm">
                        <thead className="bg-blue-50 sticky top-[52px] z-10">
                          <tr>
                            <th className="px-3 py-2 text-left border-b text-xs font-medium text-blue-800">Object Type</th>
                            <th className="px-3 py-2 text-left border-b text-xs font-medium text-blue-800">Attribute</th>
                            <th className="px-3 py-2 text-left border-b text-xs font-medium text-blue-800">Value</th>
                            <th className="px-3 py-2 text-right border-b text-xs font-medium text-blue-800">Count</th>
                            <th className="px-3 py-2 text-right border-b text-xs font-medium text-blue-800">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.keys(groupedFiltered).length > 0 ? (
                            Object.keys(groupedFiltered).map((objType, objTypeIndex) => (
                              Object.keys(groupedFiltered[objType]).map((attr, attrIndex) => (
                                <React.Fragment key={`filtered-${objType}-${attr}`}>
                                  {/* Value Rows - Top 10 only, first row has thick top border */}
                                  {groupedFiltered[objType][attr].slice(0, 10).map((stat, idx) => {
                                    const isFirstObjectType = objTypeIndex === 0;
                                    const isObjectTypeStart = attrIndex === 0 && idx === 0 && !isFirstObjectType;
                                    const isAttributeStart = idx === 0 && attrIndex > 0;

                                    return (
                                      <tr
                                        key={`filtered-${objType}-${attr}-${idx}`}
                                        className="border-b hover:bg-blue-50"
                                      >
                                        <td className={`px-3 py-1 ${isObjectTypeStart ? 'border-t-4 border-t-blue-600' : ''}`}>
                                          <span className="inline-block px-2 py-0.5 text-xs rounded bg-blue-50 text-blue-700">
                                            {objType}
                                          </span>
                                        </td>
                                        <td className={`px-3 py-1 text-xs text-blue-800 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-blue-600' : ''}`}>{attr}</td>
                                        <td className={`px-3 py-1 text-xs text-gray-600 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-blue-600' : ''}`}>{stat.value}</td>
                                        <td className={`px-3 py-1 text-right text-xs text-gray-600 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-blue-600' : ''}`}>{stat.count.toLocaleString()}</td>
                                        <td className={`px-3 py-1 text-right text-xs text-gray-600 ${isObjectTypeStart || isAttributeStart ? 'border-t-4 border-t-blue-600' : ''}`}>{stat.percentage.toFixed(2)}%</td>
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              ))
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="px-3 py-8 text-center text-gray-500 text-xs">
                                No statistics found after filtering
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>

            </>
          )}
        </>
      )}

      </div>
      {/* End of scrollable container */}

      {/* Footer Actions - Next Button (only show when filters are active and analysis is done) */}
      {statistics && statistics.has_active_filters && (
        <div className="flex justify-end items-center mt-4 flex-shrink-0">
          <button
            onClick={async () => {
              setAnalyzingRootCause(true);
              try {
                const payload = {
                  file_type: activeFile,
                  selected_object_types: selectedObjectTypes,
                  selected_attributes: selectedAttributes,
                  date_range: dateRange && dateRange.enabled && (dateRange.startDate || dateRange.endDate) ? {
                    start_date: dateRange.startDate,
                    end_date: dateRange.endDate,
                    enabled: true
                  } : undefined,
                  edge_duration_filters: edgeDurationFilters && edgeDurationFilters.length > 0 ? edgeDurationFilters : undefined,
                  edge_containment_filters: edgeContainmentFilters && edgeContainmentFilters.edges && edgeContainmentFilters.edges.length > 0
                    ? edgeContainmentFilters
                    : undefined,
                  activity_node_filters: activityNodeFilters && activityNodeFilters.nodes && activityNodeFilters.nodes.length > 0
                    ? activityNodeFilters
                    : undefined,
                  event_attribute_filter: (eventAttributeFilters.enabled && eventAttributeFilters.conditions.some(c => c.attribute && c.value))
                    ? eventAttributeFilters
                    : undefined,
                  object_attribute_filter: (objectAttributeFilters.enabled && objectAttributeFilters.conditions.some(c => c.attribute && c.value))
                    ? objectAttributeFilters
                    : undefined,
                  significance_threshold: 0.05
                };

                const results = await makePostRequest('/rootcause/analyze-step3', payload);

                // Navigate to Step 4 with results
                onNext({
                  dateRange,
                  edgeDurationFilters,
                  edgeContainmentFilters,
                  activityNodeFilters,
                  eventAttributeFilters,
                  objectAttributeFilters,
                  rootCauseResults: results,
                  statistics,
                  expandedSections,
                });
              } catch (err) {
                console.error('Error performing root cause analysis:', err);
                setError(err.message || 'Failed to perform root cause analysis');
              } finally {
                setAnalyzingRootCause(false);
              }
            }}
            disabled={analyzingRootCause}
            className={`px-6 py-2 rounded font-medium ${
              analyzingRootCause
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {analyzingRootCause ? 'Calculating...' : 'Next: Perform Root Cause Analysis →'}
          </button>
        </div>
      )}
    </div>
  );
};

export default Step3_FilterAndStats;
